import type {PayloadAction} from '@reduxjs/toolkit';
import {createSelector, createSlice} from '@reduxjs/toolkit';
import type {PosterObject, PosterPageObject} from '@PosterWhiteboard/poster/poster.types';
import {PAGE_END_TIME_TOLERANCE_SECONDS} from '@PosterWhiteboard/poster/poster.types';
import type {CopyableItemStyles, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {AiState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {AiImageState, AiSubtitleState, AiVoiceState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {ActivityIndicatorType} from '@Components/activity-indicator/activity-indicator.types';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {AudioClipsObject} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';
import type {SuggestionItems} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown.types';
import type {RootState} from '@/store';
import type {DeepPartial} from '@/global';
import type {FancyTextItemObject} from '@PosterWhiteboard/items/fancy-text-item/fancy-text-item.class';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import type {FeatureToShowOnLoad} from '@Components/poster-editor/poster-editor.types';
import {FeatureDemo, MobilePageSelectionModeState} from '@Components/poster-editor/poster-editor.types';
import type {UserSaveConflictPopupProps} from '@Components/user-save-conflict-popup';
import type {PosterVersionData} from '@Components/poster-editor/library/poster-editor-library';
import {isPageVideo as isPageVideoUtil} from '@Libraries/page-object.library';
import type {ItemLoadingProgressType} from '@PosterWhiteboard/items/item/item.types';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';
import type {PageItemsObject} from '@PosterWhiteboard/page/page-items.class';
import type {PageBackgroundObject} from '@PosterWhiteboard/page/page-background.class';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import {shallowEqual} from 'react-redux';

export interface UpdatePosterDataProps {
  posterObject?: PosterObject;
  hasUndo?: boolean;
  hasUnsavedChanges?: boolean;
  hasRedo?: boolean;
  isVideo?: boolean;
  currentPageId?: string;
  activeItemIds?: string[];
  isPosterVideo?: boolean;
}

interface UpdateIsTimelineModeActiveProps {
  isTimelineModeActive: boolean;
  pageIdToScrollToOnWhiteboardResize?: string;
}

interface UpdatePageDataPayload {
  pageHashId: string;
  obj: DeepPartial<PageObject>;
}

interface UpdateItemDataPayload {
  pageHashId: string;
  itemHashId: string;
  obj: DeepPartial<ItemObject>;
}

interface UpdateAudioItemIsPlayingPayload {
  audioItemUID: string;
  isPlaying: boolean;
}

interface UpdateArePosterItemsModifyingProps {
  pageHashedId: string;
  isModifying: boolean;
}

export interface UpdateShouldHideControlsProps {
  activeItemHashId: string;
  shouldHideControls: boolean;
}

interface UpdateActiveItemsOnPagePayload {
  pageHashedId: string;
  activeItemIds: string[];
}

interface UpdateHoveredItemProps {
  pageHashedId: string;
  hoveredItemId: string | undefined;
}

export interface ItemLoadingState {
  isVisible: boolean;
  progress: number;
  langTextKeys?: string[];
  progressType?: ItemLoadingProgressType;
}

interface UpdateItemLoadingStatePayload {
  pageHashedId: string;
  itemId: string;
  state: ItemLoadingState | null;
}

interface StateProps {
  posterObject: PosterObject | null;
  hasUndo: boolean;
  isPosterSaving: boolean;
  didPosterSaveFailed: boolean;
  arePosterItemsMoving: Record<string, boolean>;
  arePosterItemsScaling: Record<string, boolean>;
  arePosterItemsRotating: Record<string, boolean>;
  shouldHideControls: Record<string, boolean>;
  didPosterRecentlySave: boolean;
  hasUnsavedChanges: boolean;
  hasRedo: boolean;
  isVideo: boolean;
  currentPageId: string;
  isEmbeddedExportMode: boolean;
  activeItemIds: Record<string, string[]>;
  drilledGroupId: Record<string, string>;
  hoveredItemId: Record<string, string>;
  isPosterVideo?: boolean;
  isSpellCheckSettingsActive?: boolean;
  isSidebarOpen: boolean;
  isSidebarSmall?: boolean;
  isMobileVariant?: boolean;
  isPickingModeEnabled?: boolean;
  isRemoveBGModeEnabled?: boolean;
  aiState: AiState;
  copiedStyles?: CopyableItemStyles;
  isUploadingToMyUploads: boolean;
  isPublishDropDownOpen: boolean;
  inputSuggestions?: SuggestionItems;
  businessAddressSuggestions?: SuggestionItems;
  showAutoSaveTooltip: boolean;
  haveShownAutoSaveTooltip: boolean;
  isPosterOverlayLoaderActive: boolean;
  isVersionHistoryOverlayActive: boolean;
  versionHistorySelectedVersion: PosterVersionData | null;
  currentUserId?: number;
  tiggerOpenTemplatesPopup?: boolean;
  featureToShowOnLoad?: FeatureToShowOnLoad;
  featureDemo?: FeatureDemo;
  triggerOpenRecordOptions?: boolean;
  isPosterShared?: boolean;
  isPosterSharedInTeam?: boolean;
  saveConflictData?: SaveConflictData;
  isVersionHistoryModeActive: boolean;
  isTimelineModeActive?: boolean;
  fitToScreenOnWhiteboardResize: boolean;
  pageIdToScrollToOnWhiteboardResize?: string;
  itemLoadingStates: Record<string, Record<string, ItemLoadingState>>;
  mobilePageSelectionModeState: MobilePageSelectionModeState;
  pagePreviews: Record<string, string>;
  triggerOpenDownloadWebPanel?: boolean;
}

interface SaveConflictData extends UserSaveConflictPopupProps {
  triggerOpenSaveConflictPopup: boolean;
}

const initialState: StateProps = {
  hasUndo: false,
  isPosterSaving: false,
  didPosterSaveFailed: false,
  arePosterItemsMoving: {},
  arePosterItemsScaling: {},
  arePosterItemsRotating: {},
  shouldHideControls: {},
  didPosterRecentlySave: false,
  hasRedo: false,
  hasUnsavedChanges: false,
  isVideo: false,
  isEmbeddedExportMode: false,
  currentPageId: '',
  posterObject: null,
  activeItemIds: {},
  drilledGroupId: {},
  hoveredItemId: {},
  isMobileVariant: isEditorMobileVariant(),
  isPosterVideo: false,
  isSpellCheckSettingsActive: false,
  isSidebarOpen: true,
  isSidebarSmall: false,
  isPickingModeEnabled: false,
  isRemoveBGModeEnabled: false,
  aiState: {
    aiImage: AiImageState.IDLE,
    aiSubtitles: AiSubtitleState.IDLE,
    aiVoice: AiVoiceState.IDLE,
  },
  isUploadingToMyUploads: false,
  isPublishDropDownOpen: false,
  showAutoSaveTooltip: false,
  haveShownAutoSaveTooltip: false,
  copiedStyles: undefined,
  isPosterOverlayLoaderActive: false,
  isVersionHistoryOverlayActive: false,
  versionHistorySelectedVersion: null,
  tiggerOpenTemplatesPopup: false,
  triggerOpenRecordOptions: false,
  featureDemo: FeatureDemo.NONE,
  isPosterShared: false,
  isPosterSharedInTeam: false,
  saveConflictData: undefined,
  isTimelineModeActive: false,
  fitToScreenOnWhiteboardResize: false,
  pageIdToScrollToOnWhiteboardResize: undefined,
  isVersionHistoryModeActive: false,
  itemLoadingStates: {},
  mobilePageSelectionModeState: MobilePageSelectionModeState.INACTIVE,
  pagePreviews: {},
  triggerOpenDownloadWebPanel: false,
};

const posterEditorSlice = createSlice({
  name: 'posterEditor',
  initialState,
  reducers: {
    updatePosterData: (state, action: PayloadAction<UpdatePosterDataProps>) => {
      if (action.payload.posterObject !== undefined) {
        state.posterObject = action.payload.posterObject;
      }
      if (action.payload.hasUndo !== undefined) {
        state.hasUndo = action.payload.hasUndo;
      }
      if (action.payload.hasUnsavedChanges !== undefined) {
        state.hasUnsavedChanges = action.payload.hasUnsavedChanges;
      }
      if (action.payload.currentPageId !== undefined) {
        state.currentPageId = action.payload.currentPageId;
      }
      if (action.payload.hasRedo !== undefined) {
        state.hasRedo = action.payload.hasRedo;
      }
      if (action.payload.isVideo !== undefined) {
        state.isVideo = action.payload.isVideo;
      }
      if (action.payload.isPosterVideo !== undefined) {
        state.isPosterVideo = action.payload.isPosterVideo;
      }
    },
    setPosterOverlayLoaderVisibility: (state, action: PayloadAction<boolean>) => {
      state.isPosterOverlayLoaderActive = action.payload;
    },
    setVersionHistoryOverlayVisibility: (state, action: PayloadAction<boolean>) => {
      state.isVersionHistoryOverlayActive = action.payload;
    },
    setVersionHistorySelectedVersion: (state, action: PayloadAction<PosterVersionData | null>) => {
      state.versionHistorySelectedVersion = action.payload;
    },
    restoreInitialData: (state) => {
      state.didPosterSaveFailed = initialState.didPosterSaveFailed;
      state.arePosterItemsScaling = initialState.arePosterItemsScaling;
      state.arePosterItemsMoving = initialState.arePosterItemsMoving;
      state.arePosterItemsRotating = initialState.arePosterItemsRotating;
      state.didPosterRecentlySave = initialState.didPosterRecentlySave;
      state.activeItemIds = initialState.activeItemIds;
      state.drilledGroupId = initialState.drilledGroupId;
      state.hoveredItemId = initialState.hoveredItemId;
      state.shouldHideControls = initialState.shouldHideControls;
      state.fitToScreenOnWhiteboardResize = initialState.fitToScreenOnWhiteboardResize;
      state.pageIdToScrollToOnWhiteboardResize = initialState.pageIdToScrollToOnWhiteboardResize;
    },
    updateHasUndo: (state, action: PayloadAction<boolean>) => {
      state.hasUndo = action.payload;
    },
    updateIsPosterSaving: (state, action: PayloadAction<boolean>) => {
      state.isPosterSaving = action.payload;
    },
    updateDidPosterSaveFailed: (state, action: PayloadAction<boolean>) => {
      state.didPosterSaveFailed = action.payload;
    },
    updateArePosterItemsMoving: (state, action: PayloadAction<UpdateArePosterItemsModifyingProps>) => {
      state.arePosterItemsMoving[action.payload.pageHashedId] = action.payload.isModifying;
    },
    updateArePosterItemsScaling: (state, action: PayloadAction<UpdateArePosterItemsModifyingProps>) => {
      state.arePosterItemsScaling[action.payload.pageHashedId] = action.payload.isModifying;
    },
    updateArePosterItemsRotating: (state, action: PayloadAction<UpdateArePosterItemsModifyingProps>) => {
      state.arePosterItemsRotating[action.payload.pageHashedId] = action.payload.isModifying;
    },
    updateShouldHideControls: (state, action: PayloadAction<UpdateShouldHideControlsProps>) => {
      state.shouldHideControls[action.payload.activeItemHashId] = action.payload.shouldHideControls;
    },
    onPosterItemsModified: (state, action: PayloadAction<string>) => {
      delete state.arePosterItemsMoving[action.payload];
      delete state.arePosterItemsScaling[action.payload];
      delete state.arePosterItemsRotating[action.payload];
    },
    updateItemData: (state, action: PayloadAction<UpdateItemDataPayload>) => {
      if (!state.posterObject) return;

      const {pageHashId, itemHashId, obj} = action.payload;
      const item = state.posterObject.pages.pagesHashMap[pageHashId]?.items.itemsHashMap[itemHashId];

      if (!item) return;

      const updates: Partial<typeof item> = {};
      if (obj.left !== undefined) updates.left = obj.left;
      if (obj.top !== undefined) updates.top = obj.top;
      if (obj.scaleX !== undefined) updates.scaleX = obj.scaleX;
      if (obj.scaleY !== undefined) updates.scaleY = obj.scaleY;
      if (obj.angle !== undefined) updates.angle = obj.angle;

      Object.assign(item, updates);
    },
    updatePageData: (state, action: PayloadAction<UpdatePageDataPayload>) => {
      if (!state.posterObject) return;

      const {pageHashId, obj} = action.payload;
      const page = state.posterObject.pages.pagesHashMap[pageHashId];

      if (!page) return;

      const updates: Partial<PageObject> = {};
      if (obj.isPlaying !== undefined) updates.isPlaying = obj.isPlaying;
      if (obj.currentTime !== undefined) updates.currentTime = obj.currentTime;

      Object.assign(page, updates);
    },
    updateDidPosterRecentlySave: (state, action: PayloadAction<boolean>) => {
      state.didPosterRecentlySave = action.payload;
    },
    updateIsEmbeddedExportMode: (state, action: PayloadAction<boolean>) => {
      state.isEmbeddedExportMode = action.payload;
    },
    updateHasRedo: (state, action: PayloadAction<boolean>) => {
      state.hasRedo = action.payload;
    },
    updateIsPosterEditorMobileVariant: (state, action: PayloadAction<boolean>) => {
      state.isMobileVariant = action.payload;
    },
    updateActiveItemsOnPage: (state, action: PayloadAction<UpdateActiveItemsOnPagePayload>) => {
      state.activeItemIds[action.payload.pageHashedId] = action.payload.activeItemIds;
    },
    setDrilledGroupId: (state, action: PayloadAction<{pageHashedId: string; groupId: string}>) => {
      state.drilledGroupId[action.payload.pageHashedId] = action.payload.groupId;
    },
    clearDrilledGroupId: (state, action: PayloadAction<string>) => {
      delete state.drilledGroupId[action.payload];
    },
    updateHoveredItem: (state, action: PayloadAction<UpdateHoveredItemProps>) => {
      if (action.payload.hoveredItemId) {
        state.hoveredItemId[action.payload.pageHashedId] = action.payload.hoveredItemId;
      } else {
        delete state.hoveredItemId[action.payload.pageHashedId];
      }
    },
    updateItemLoadingState: (state, action: PayloadAction<UpdateItemLoadingStatePayload>) => {
      const {pageHashedId, itemId, state: loadingState} = action.payload;

      if (loadingState) {
        if (!state.itemLoadingStates[pageHashedId]) {
          state.itemLoadingStates[pageHashedId] = {};
        }
        state.itemLoadingStates[pageHashedId][itemId] = loadingState;
      } else {
        if (state.itemLoadingStates[pageHashedId]) {
          delete state.itemLoadingStates[pageHashedId][itemId];
          if (Object.keys(state.itemLoadingStates[pageHashedId]).length === 0) {
            delete state.itemLoadingStates[pageHashedId];
          }
        }
      }
    },
    setSelectedAudioItemUID: (state, action: PayloadAction<string>) => {
      if (state.posterObject) {
        const currentPage = state.posterObject.pages.pagesHashMap[state.currentPageId];
        if (currentPage) {
          currentPage.audioClips.selectedAudioItemUID = action.payload;
        }
      }
    },
    updateAudioItemIsPlaying: (state, action: PayloadAction<UpdateAudioItemIsPlayingPayload>) => {
      if (state.posterObject) {
        const currentPage = state.posterObject.pages.pagesHashMap[state.currentPageId];
        if (currentPage?.audioClips.audioItemsHashMap[action.payload.audioItemUID]) {
          currentPage.audioClips.audioItemsHashMap[action.payload.audioItemUID].audioPlayer.isPlaying = action.payload.isPlaying;
        }
      }
    },
    updateSpellCheckSettingsState: (state, action: PayloadAction<boolean>) => {
      state.isSpellCheckSettingsActive = action.payload;
    },
    updateSidebarState: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },
    updateCollapsableSidebarState: (state, action: PayloadAction<boolean>) => {
      state.isSidebarSmall = action.payload;
    },
    updatePickingMode: (state, action: PayloadAction<boolean>) => {
      state.isPickingModeEnabled = action.payload;
    },
    updateRemoveBgMode: (state, action: PayloadAction<boolean>) => {
      state.isRemoveBGModeEnabled = action.payload;
    },
    updateAiImageState: (state, action: PayloadAction<AiImageState>) => {
      state.aiState.aiImage = action.payload;
    },
    updateAiSubtitleState: (state, action: PayloadAction<AiSubtitleState>) => {
      state.aiState.aiSubtitles = action.payload;
    },
    updateAiVoiceState: (state, action: PayloadAction<AiVoiceState>) => {
      state.aiState.aiVoice = action.payload;
    },
    updateIsUploadingToMyUploads: (state, action: PayloadAction<boolean>) => {
      if (state.isUploadingToMyUploads !== action.payload) {
        state.isUploadingToMyUploads = action.payload;
      }
    },
    updatePublishDropDownState: (state, action: PayloadAction<boolean>) => {
      state.isPublishDropDownOpen = action.payload;
    },
    setInputSuggestions: (state, action: PayloadAction<SuggestionItems>) => {
      state.inputSuggestions = action.payload;
    },
    setBusinessAddress: (state, action: PayloadAction<SuggestionItems>) => {
      state.businessAddressSuggestions = action.payload;
    },
    updateAutoSaveTooltipState: (state, action: PayloadAction<boolean>) => {
      state.showAutoSaveTooltip = action.payload;
      if (action.payload) {
        state.haveShownAutoSaveTooltip = true;
      }
    },
    updateCopiedStylesState: (state, action: PayloadAction<CopyableItemStyles | undefined>) => {
      state.copiedStyles = action.payload;
    },
    updateCurrentUserId: (state, action: PayloadAction<number>) => {
      state.currentUserId = action.payload;
    },
    updateFeatureToShowOnLoad: (state, action: PayloadAction<FeatureToShowOnLoad>) => {
      state.featureToShowOnLoad = action.payload;
    },
    updateFeatureDemo: (state, action: PayloadAction<FeatureDemo>) => {
      state.featureDemo = action.payload;
    },
    updateRecordOptionsOpenState: (state, action: PayloadAction<boolean>) => {
      state.triggerOpenRecordOptions = action.payload;
    },
    updateIsPosterShared: (state, action: PayloadAction<boolean>) => {
      state.isPosterShared = action.payload;
    },
    updateIsPosterSharedInTeam: (state, action: PayloadAction<boolean>) => {
      state.isPosterSharedInTeam = action.payload;
    },
    updateSaveConflictDataDetails: (state, action: PayloadAction<SaveConflictData | undefined>) => {
      state.saveConflictData = action.payload;
    },
    updateVersionHistoryModeState: (state, action: PayloadAction<boolean>) => {
      state.isVersionHistoryModeActive = action.payload;
    },
    updatePageIdToScrollToOnWhiteboardResize: (state, action: PayloadAction<string | undefined>) => {
      state.pageIdToScrollToOnWhiteboardResize = action.payload;
    },
    updateFitToScreenOnWhiteboardResize: (state, action: PayloadAction<boolean>) => {
      state.fitToScreenOnWhiteboardResize = action.payload;
    },
    updateIsTimelineModeActive: (state, action: PayloadAction<UpdateIsTimelineModeActiveProps>) => {
      state.isTimelineModeActive = action.payload.isTimelineModeActive;
      state.fitToScreenOnWhiteboardResize = true;
      state.pageIdToScrollToOnWhiteboardResize = action.payload.pageIdToScrollToOnWhiteboardResize;
    },
    updatePosterScale: (state, action: PayloadAction<number>) => {
      if (state.posterObject) {
        state.posterObject.scaling.scale = action.payload;
      }
    },
    setMobilePageSelectionModeState: (state, action: PayloadAction<MobilePageSelectionModeState>) => {
      state.mobilePageSelectionModeState = action.payload;
    },
    updatePagePreview: (state, action: PayloadAction<{pageHashedId: string; previewUrl: string}>) => {
      state.pagePreviews[action.payload.pageHashedId] = action.payload.previewUrl;
    },
    removePagePreview: (state, action: PayloadAction<string>) => {
      delete state.pagePreviews[action.payload];
    },
    updateWebDownloadPanelOpenState: (state, action: PayloadAction<boolean>) => {
      state.triggerOpenDownloadWebPanel = action.payload;
    },
  },
});

export const {
  updatePosterData,
  setPosterOverlayLoaderVisibility,
  setVersionHistoryOverlayVisibility,
  setVersionHistorySelectedVersion,
  restoreInitialData,
  updateAudioItemIsPlaying,
  updateIsEmbeddedExportMode,
  setSelectedAudioItemUID,
  updateActiveItemsOnPage,
  setDrilledGroupId,
  clearDrilledGroupId,
  updateHoveredItem,
  updateItemLoadingState,
  updateHasUndo,
  updateDidPosterRecentlySave,
  updateIsPosterSaving,
  updateDidPosterSaveFailed,
  updateArePosterItemsMoving,
  updateArePosterItemsRotating,
  updateArePosterItemsScaling,
  updateShouldHideControls,
  onPosterItemsModified,
  updateItemData,
  updatePageData,
  updateHasRedo,
  updateSpellCheckSettingsState,
  updateSidebarState,
  updateCollapsableSidebarState,
  updatePickingMode,
  updateRemoveBgMode,
  updateIsPosterEditorMobileVariant,
  updateAiImageState,
  updateAiSubtitleState,
  updateAiVoiceState,
  updateIsUploadingToMyUploads,
  updatePublishDropDownState,
  setInputSuggestions,
  setBusinessAddress,
  updateCopiedStylesState,
  updateAutoSaveTooltipState,
  updateCurrentUserId,
  updateRecordOptionsOpenState,
  updateFeatureToShowOnLoad,
  updateFeatureDemo,
  updateIsPosterShared,
  updateIsPosterSharedInTeam,
  updateSaveConflictDataDetails,
  updateVersionHistoryModeState,
  updatePageIdToScrollToOnWhiteboardResize,
  updateFitToScreenOnWhiteboardResize,
  updateIsTimelineModeActive,
  updatePosterScale,
  setMobilePageSelectionModeState,
  updatePagePreview,
  removePagePreview,
  updateWebDownloadPanelOpenState,
} = posterEditorSlice.actions;
export const posterEditorReducer = posterEditorSlice.reducer;

export const getSelectedAudioItem = createSelector(
  [
    (state: RootState): Record<string, AudioItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips.audioItemsHashMap;
    },
    (state: RootState): string | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips.selectedAudioItemUID;
    },
  ],
  (audioItemHashMap, selectedAudioItemUID) => {
    if (!audioItemHashMap || !selectedAudioItemUID) {
      return undefined;
    }

    return audioItemHashMap[selectedAudioItemUID];
  }
);

export const getAudioItemFromCurrentPageById = createSelector(
  [
    (state: RootState): Record<string, AudioItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips?.audioItemsHashMap;
    },
    (_state: RootState, audioItemId: string): string => audioItemId,
  ],
  (audioItemsHashMap, audioItemId) => {
    if (!audioItemsHashMap || !audioItemId) {
      return undefined;
    }
    return audioItemsHashMap[audioItemId];
  }
);

export const getSelectedAudioItemHasFade = createSelector([getSelectedAudioItem], (audioItem): boolean => {
  if (!audioItem) {
    return false;
  }
  return audioItem.audioPlayer.fade.fadeInDuration !== 0 || audioItem.audioPlayer.fade.fadeOutDuration !== 0;
});

export const getPosterObject = (state: RootState): PosterObject | null => {
  return state.posterEditor.posterObject;
};

export const getCopiedStyles = (state: RootState): CopyableItemStyles | undefined => {
  return state.posterEditor.copiedStyles;
};

export const selectItem = createSelector(
  [
    (state: RootState): Record<string, PageObject> | undefined => {
      return state.posterEditor.posterObject?.pages?.pagesHashMap;
    },
    (_state: RootState, itemId: string): string => itemId,
  ],
  (pages, itemId) => {
    if (!pages) {
      return undefined;
    }

    for (const [, page] of Object.entries(pages)) {
      if (page.items.itemsHashMap[itemId]) {
        return page.items.itemsHashMap[itemId];
      }
    }

    return undefined;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const isPagePlaying = (state: RootState, pageId: string): boolean => {
  return state.posterEditor.posterObject?.pages?.pagesHashMap[pageId]?.isPlaying ?? false;
};

export const getIsCurrentPagePlaying = (state: RootState): boolean => {
  const currentPageId = state.posterEditor.currentPageId;
  return state.posterEditor.posterObject?.pages?.pagesHashMap[currentPageId]?.isPlaying ?? false;
};

export const isPagePlaybackEnded = createSelector(
  [
    (state: RootState, pageId: string): number | undefined => state.posterEditor.posterObject?.pages?.pagesHashMap[pageId]?.duration,
    (state: RootState, pageId: string): number => state.posterEditor.posterObject?.pages?.pagesHashMap[pageId]?.currentTime ?? 0,
    (_state: RootState, pageId: string): string => pageId,
  ],
  (pageDuration, currentTime) => {
    if (pageDuration === undefined) {
      return false;
    }

    return Math.abs(currentTime - pageDuration) < PAGE_END_TIME_TOLERANCE_SECONDS;
  }
);

export const getPagePlaybackIcon = createSelector(
  [
    (state: RootState, pageId: string): boolean => isPagePlaying(state, pageId),
    (state: RootState, pageId: string): boolean => isPagePlaybackEnded(state, pageId),
    (_state: RootState, pageId: string): string => pageId,
  ],
  (playing, ended) => {
    if (playing) {
      return 'icon-pause';
    }

    if (ended) {
      return 'icon-replay-video';
    }

    return 'icon-play-video';
  }
);

export const getCurrentPageDuration = (state: RootState): number | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.duration;
};

export const getCurrentPageTime = (state: RootState): number => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return 0;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.currentTime ?? 0;
};

export const getCurrentPageTimeFloored = (state: RootState): number => {
  return Math.floor(getCurrentPageTime(state));
};

export const getPageCurrentTime = (state: RootState, pageId: string): number => {
  if (!pageId) {
    return 0;
  }
  return state.posterEditor.posterObject?.pages?.pagesHashMap[pageId]?.currentTime ?? 0;
};

export const getPosterDuration = createSelector(
  [
    (state: RootState): Record<string, PageObject> | undefined => {
      return state.posterEditor.posterObject?.pages?.pagesHashMap;
    },
  ],
  (pages) => {
    if (!pages) {
      return undefined;
    }

    let totalDuration = 0;
    for (const [, page] of Object.entries(pages)) {
      totalDuration += page.duration;
    }

    return totalDuration;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getCurrentPageBackground = (state: RootState): PageBackgroundObject | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.background;
};

export const getCurrentPageHashedID = (state: RootState): string | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.hashedID;
};


export const getCurrentPageItems = (state: RootState): PageItemsObject | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items;
};

export const getCurrentPageAppliedPalette = (state: RootState): ColorPaletteInfo | undefined => {
  const currentPageId = state.posterEditor.currentPageId;
  if (!currentPageId) {
    return undefined;
  }
  return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.appliedPalette;
};

export const getNumberOfPages = (state: RootState): number => {
  if (!state.posterEditor.posterObject?.pages.pagesHashMap) {
    return 0;
  }
  return Object.keys(state.posterEditor.posterObject.pages.pagesHashMap).length;
};

export const isScaleAnimatingToFitToScreen = createSelector(
  [
    (state: RootState): boolean => {
      return state.posterEditor.posterObject?.scaling.isScaleAnimating === true;
    },
    (state: RootState): boolean => {
      return state.posterEditor.posterObject?.scaling.isCurrentScaleBestFit === true;
    },
  ],
  (isScaleAnimating, isCurrentScaleBestFit) => {
    return isScaleAnimating && isCurrentScaleBestFit;
  }
);

export const getNumberOfItemsInCurrentPage = createSelector(
  [
    (state: RootState): Record<string, ItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap;
    },
  ],
  (itemsHashMap) => {
    if (!itemsHashMap) {
      return 0;
    }

    return Object.keys(itemsHashMap).length;
  }
);

export const getAllTranscriptItemObjects = createSelector(
  [
    (state: RootState): Record<string, PageObject> | undefined => {
      return state.posterEditor.posterObject?.pages.pagesHashMap;
    },
  ],
  (pages) => {
    if (!pages) {
      return {};
    }
    const transcriptItemObjectsHashmap: Record<string, TranscriptItemObject> = {};
    for (const page of Object.values(pages)) {
      for (const item of Object.values(page.items.itemsHashMap)) {
        if (item.gitype === ITEM_TYPE.TRANSCRIPT) {
          transcriptItemObjectsHashmap[item.uid] = item as TranscriptItemObject;
        }
      }
    }

    return transcriptItemObjectsHashmap;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getTranscriptItemsInCurrentPage = createSelector(
  [
    (state: RootState): Record<string, ItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap;
    },
  ],
  (itemsHashMap): TranscriptItemObject[] => {
    if (!itemsHashMap) {
      return [];
    }
    return Object.values(itemsHashMap).filter((item): item is TranscriptItemObject => item.gitype === ITEM_TYPE.TRANSCRIPT);
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getAllSelectedItemObjectsOnCurrentPage = createSelector(
  [
    (state: RootState): Record<string, ItemObject> | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items.itemsHashMap;
    },
    (state: RootState): string[] | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.activeItemIds[currentPageId];
    },
  ],
  (itemsHashMap, selectedIdsOnPage) => {
    if (!itemsHashMap || !selectedIdsOnPage || selectedIdsOnPage.length === 0) {
      return {};
    }

    const selectedItemObjects: Record<string, ItemObject> = {};
    for (const id of selectedIdsOnPage) {
      const item = itemsHashMap[id];
      if (item) {
        selectedItemObjects[id] = item;
      }
    }

    return selectedItemObjects;
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

/**
 * returns item object if it is the only item selected otherwise returns null
 */
export const getSingleActiveItemObjectOnPage = (state: RootState, pageId?: string): ItemObject | null => {
  const activeItemIdsOnPage = state.posterEditor.activeItemIds[pageId ?? state.posterEditor.currentPageId];
  if (!activeItemIdsOnPage || activeItemIdsOnPage.length !== 1) {
    return null;
  }

  return state.posterEditor.posterObject?.pages.pagesHashMap[pageId ?? state.posterEditor.currentPageId].items.itemsHashMap[activeItemIdsOnPage[0]] ?? null;
};

export const getCurrentPageVideoItemsWithAudioInOrder = createSelector(
  [
    (state: RootState): PageItemsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages.pagesHashMap[currentPageId]?.items;
    },
  ],
  (items): VideoItemObject[] => {
    if (!items) {
      return [];
    }

    const videoItems: VideoItemObject[] = [];

    for (const itemId of items.itemsOrder) {
      const item = items.itemsHashMap[itemId];
      if (item.gitype === ITEM_TYPE.VIDEO) {
        videoItems.push(item as VideoItemObject);
      }
    }

    return videoItems;
  }
);

// returns audio items in order of their onPageStartTime (using name as a tie-breaker)
export const getAudioItemsInOrder = createSelector(
  [
    (state: RootState): AudioClipsObject | undefined => {
      const currentPageId = state.posterEditor.currentPageId;
      return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips;
    },
  ],
  (audioClipsObject): AudioItemObject[] => {
    const audioItems: AudioItemObject[] = [];
    if (audioClipsObject) {
      audioItems.push(...Object.values(audioClipsObject.audioItemsHashMap));
    }

    audioItems.sort((a, b) => {
      if (a.onPageStartTime !== b.onPageStartTime) {
        return a.onPageStartTime - b.onPageStartTime;
      }
      return a.name.localeCompare(b.name);
    });

    return audioItems;
  }
);

export const getTextItems = createSelector(
  [
    (state: RootState): PosterPageObject | undefined => {
      return state.posterEditor.posterObject?.pages;
    },
  ],
  (pages): (TextItemObject | FancyTextItemObject)[] => {
    if (!pages) return [];

    return Object.values(pages.pagesHashMap).flatMap((page) => {
      const items = Object.values(page.items.itemsHashMap);

      return items.filter((item): item is TextItemObject | FancyTextItemObject => {
        return item.gitype === ITEM_TYPE.TEXT || item.gitype === ITEM_TYPE.FANCY_TEXT;
      });
    });
  },
  {memoizeOptions: {resultEqualityCheck: shallowEqual}}
);

export const getActivityIndicatorTypeForAi = createSelector(
  [
    (state: RootState): AiState => {
      return state.posterEditor.aiState;
    },
  ],
  (aiState): ActivityIndicatorType | undefined => {
    if (aiState.aiImage === AiImageState.GENERATING || aiState.aiSubtitles === AiSubtitleState.GENERATING) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiState.aiSubtitles === AiSubtitleState.READY_FAILED) {
      return ActivityIndicatorType.ERROR;
    }
    if (aiState.aiImage === AiImageState.READY || aiState.aiSubtitles === AiSubtitleState.READY_SUCCESS) {
      return ActivityIndicatorType.SUCCESS;
    }
    return undefined;
  }
);

export const getActivityIndicatorTypeForAiImage = createSelector(
  [
    (state: RootState): AiImageState => {
      return state.posterEditor.aiState.aiImage;
    },
  ],
  (aiImageStatus): ActivityIndicatorType | undefined => {
    if (aiImageStatus === AiImageState.GENERATING) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiImageStatus === AiImageState.READY) {
      return ActivityIndicatorType.SUCCESS;
    }
    return undefined;
  }
);

export const getActivityIndicatorTypeForAiSubtitle = createSelector(
  [
    (state: RootState): AiSubtitleState => {
      return state.posterEditor.aiState.aiSubtitles;
    },
  ],
  (aiSubtitleStatus): ActivityIndicatorType | undefined => {
    if (aiSubtitleStatus === AiSubtitleState.GENERATING) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiSubtitleStatus === AiSubtitleState.READY_SUCCESS) {
      return ActivityIndicatorType.SUCCESS;
    }
    if (aiSubtitleStatus === AiSubtitleState.READY_FAILED) {
      return ActivityIndicatorType.ERROR;
    }
    return undefined;
  }
);

export const getActivityIndicatorTypeForAiVoice = createSelector(
  [
    (state: RootState): AiVoiceState => {
      return state.posterEditor.aiState.aiVoice;
    },
  ],
  (aiVoiceStatus): ActivityIndicatorType | undefined => {
    if (aiVoiceStatus === AiVoiceState.GENERATING) {
      return ActivityIndicatorType.LOADING;
    }
    if (aiVoiceStatus === AiVoiceState.READY) {
      return ActivityIndicatorType.SUCCESS;
    }
    if (aiVoiceStatus === AiVoiceState.ERROR) {
      return ActivityIndicatorType.ERROR;
    }
    return undefined;
  }
);

export const isPageVideo = (state: RootState, pageId: string): boolean => {
  const page = state.posterEditor.posterObject?.pages?.pagesHashMap[pageId];
  if (!page) {
    return false;
  }
  return isPageVideoUtil(page);
};
